import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';
import { Roles } from 'constants/authRoles';
import { varToStringParams } from 'utils/path';
import { IDocument, IDocumentPayload } from 'constants/document';
import { INote, NoteType } from 'constants/notes';
import { IProductListOutput } from 'services/api/product';
import SharingType from 'constants/sharing';
import CustomerAttribute from 'constants/customer';
import moment from 'moment';

export interface IPaging {
  page: number;
  pageSize: number;
  total: number;
}

export interface IClientListItem {
  id: number;
  customerguid: string;
  firstname: string;
  lastname: string;
  gender: number | null;
  dateofbirth: Date | null;
  username: string | null;
  email: string;
  emailtorevalidate: boolean | null;
  failedloginattempts: number | null;
  cannotloginuntildateutc: Date | null;
  active: boolean;
  deleted: boolean;
  issystemaccount: boolean | null;
  systemname: string | null;
  lastipaddress: string | null;
  lastlogindateutc: Date;
  lastactivitydateutc: Date | null;
  createdby: { id: number; name: string; picture: string }[];
  creator: string;
  createdon: Date;
  picture?: string | null;
}

export interface ICategoryListItem {
  id: number;
  name: string;
  description: string | null;
  categorytemplateid: number;
  metakeywords: string | null;
  metadescription: string | null;
  metatitle: string | null;
  parentcategoryid: number;
  pictureid: number | null;
  pagesize: number | null;
  allowcustomerstoselectpagesize: boolean;
  pagesizeoptions: string;
  priceranges: string | null;
  showonhomepage: boolean;
  includeintopmenu: boolean;
  subjecttoacl: boolean;
  limitedtostores: boolean;
  published: boolean;
  deleted: boolean;
  displayorder: number;
  createdonutc: Date | string;
  updatedonutc: Date | string;
  picture: string | null;
}

export interface IClientsListInput {
  sortField?: string;
  page?: number;
  pageSize?: number;
  order?: 'asc' | 'desc';
  roles?: Array<number | string>;
  agents?: Array<number | string>;
  name?: string;
  companynames?: Array<string>;
  createdby?: Array<number>;
  dateAddedFrom?: string;
  dateAddedTo?: string;
  foundUsOn?: Array<string>;
  cityId?: Array<number>;
  municipalityId?: Array<number>;
}

interface IClientsListOutput extends Types.ResponseOutput {
  data: {
    items: Array<IClientListItem>;
    paging: IPaging;
  };
}

interface ICategoryListOutput extends Types.ResponseOutput {
  data: {
    items: Array<ICategoryListItem>;
    paging: IPaging;
  };
}

export const fetchClientList = async (
  payload: IClientsListInput
): Promise<IClientsListOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'roles', value: payload?.roles?.join(',') },
      { key: 'agents', value: payload?.agents?.join(',') },
      {
        key: 'name',
        value: payload.name,
      },
      { key: 'companynames', value: payload?.companynames?.join(',') },
      { key: 'createdby', value: payload?.createdby?.join(',') },
      {
        key: 'dateAddedFrom',
        value: payload?.dateAddedFrom
          ? moment(payload?.dateAddedFrom).utc().format()
          : '',
      },
      {
        key: 'dateAddedTo',
        value: payload?.dateAddedTo
          ? moment(payload?.dateAddedTo).utc().format()
          : '',
      },
      { key: 'foundUsOn', value: payload?.foundUsOn?.join(',') },
      { key: 'cityId', value: payload?.cityId?.join(',') ?? '' },
      {
        key: 'municipalityId',
        value: payload?.municipalityId?.join(',') ?? '',
      },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/list${query}`
  );

  return data;
};

export const fetchCategoryList = async (
  payload: ICategoryInput
): Promise<ICategoryListOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/category/list${query}`
  );

  return data;
};

/** Fetch Agents */
export interface IAgent {
  id: number;
  name: string;
  email: string;
  username: string;
}

export interface IAgentOuput extends Types.ResponseOutput {
  data: Array<IAgent>;
}

export const fetchAgents = async (): Promise<IAgentOuput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/agents/mini-profile`
  );

  return data;
};

/** Fetch Customers */
export type ICustomersMiniProfileData = Array<{
  id: number;
  name: string;
  email: string;
  username: string;
}>;

export interface ICustomersMiniProfileOutput extends Types.ResponseOutput {
  data: ICustomersMiniProfileData;
}

export const fetchCustomers =
  async (): Promise<ICustomersMiniProfileOutput> => {
    const { data }: any = await request(
      'get',
      `${Environment.API_BASE_URL}/api/customer/mini-profiles`
    );

    return data;
  };

/** Delete customer */

export const deleteCustomer = async (
  customerId: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/customer/${customerId}`
  );

  return data;
};

/** Fetch Roles */
export interface IRole {
  id: number;
  name: string;
  color: string;
}

interface IRolesOutput extends Types.ResponseOutput {
  data: Array<IRole>;
}

export const fetchRoles = async (): Promise<IRolesOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/roles`
  );

  return data;
};

/** Add Client */
export interface IAddCLientInput {
  firstname: string;
  lastname: string;
  email: string;
  mobileNumber: string[];
  roles: (string | number)[];
  foundUsOn: string;
  referredBy: number[];
  agentId: number[];
  note: string;
  profile?: {
    mimetype: string;
    seofilename?: string;
    altattribute?: string;
    titleattribute?: string;
    virtualpath: string;
    originalfilename: string;
    filesize: number;
    visible: boolean;
    filetype: number;
    filecontenttype: number;
  };
  emails: string[];
  representative: number[];
}

export const addClient = async (
  payload: IAddCLientInput
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/customer`,
    {
      body: payload,
    }
  );
  return data;
};

/** Client Details */
export interface IClientDetailsData {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  lastlogin: string;
  pictureid: string;
  picture?: string;
  profile: string;
  founduson: string[];
  note: string;
  createdon: string;
  createdby: string;
  creator: string;
  statusid: number;
  status: string;
  readinesslevelid: number;
  readinesslevel: string;
  Storeid: number;
  mobileNumber: string[];
  emails: string[];
  agents: number[];
  referredBy:
    | number[]
    | { id: number; email: string; name: string; username: string | null }[];
  representative:
    | number[]
    | { id: number; email: string; name: string; username: string | null }[];
  roles: IRole[] | null;
  lastactivitydateutc: string;
  tags: { id: number; name: string }[];
  specs: {
    id: number;
    name: string;
    specificationattributeid: number;
    systemname: string;
    type: number;
    value: string;
  }[];
  companyname?: string[];
  jobposition?: string[];
  industry: string[];
}

interface IClientsDetailsOutput extends Types.ResponseOutput {
  data: IClientDetailsData;
}

export const fetchClientDetails = async (
  clientid: string
): Promise<IClientsDetailsOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientid}/details`
  );

  return data;
};

/** Edit Status */

interface IUpdateStatusOutput extends Types.ResponseOutput {
  data: null;
}

export interface IUpdateStatusPayload {
  status: number | null;
  readinessLevel: number | null;
}

export const updateStatus = async (
  clientid: string,
  body: IUpdateStatusPayload
): Promise<IUpdateStatusOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/customer/${clientid}/status`,
    {
      body,
      showToastMessage: true,
    }
  );

  return data;
};

/** Edit Client */
export interface IEditCLientInput {
  firstname?: string;
  lastname?: string;
  email?: string;
  mobileNumber?: string[];
  roles?: (string | number)[];
  foundUsOn?: string[];
  referredBy?: number[];
  representative?: number[];
  agentId?: number[];
  note?: string;
  picture?: any;
  profile?: {
    mimetype: string;
    seofilename?: string;
    altattribute?: string;
    titleattribute?: string;
    virtualpath: string;
    originalfilename: string;
    filesize: number;
    visible: boolean;
    filetype: number;
    filecontenttype: number;
  };
  tags?: { id: number; name: string }[];
  specs?: { systemname: string; value: string }[];
  companyname?: string[];
  jobposition?: string[];
  industry?: string[];
}

export const editClient = async (
  clientId: string | number,
  payload: IEditCLientInput
): Promise<Types.ResponseOutput> => {
  const { data } = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/details`,
    {
      body: payload,
      showToastMessage: true,
    }
  );
  return data;
};

export interface IInsertClientNoteBody {
  pictures?: Array<IDocumentPayload>;
  note: string;
  type: number;
}

export const insertClientNote = async (
  body: IInsertClientNoteBody,
  clientId: string
) => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/note`,
    { body }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const fetchClientNotes = async (clientId: string, limit?: number) => {
  const queryData = [{ key: 'type', value: NoteType.internalNote }];
  if (limit) {
    queryData.push({ key: 'limit', value: limit });
  }
  let query = varToStringParams({
    variablesArray: queryData,
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/note${query}`
  );
  let _data: Array<INote> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchClientPublicNotes = async (clientId: string) => {
  let query = varToStringParams({
    variablesArray: [{ key: 'type', value: NoteType.externalNote }],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/note${query}`
  );
  let _data: Array<INote> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const deleteNote = async (noteId: number) => {
  await request('delete', `${Environment.API_BASE_URL}/api/note/${noteId}`);
};

export const getClientDocuments = async (clientId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/documents`
  );
  const _data = data.data as Array<IDocument>;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const insertClientImages = async (
  clientId: number,
  documents: Array<IDocumentPayload>
) => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/documents?deletePrevious=false`,
    {
      body: {
        documents,
      },
    }
  );
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface IUpdateDocumentArrangementsPayload {
  pictureId: number;
  displayOrder: number;
}
export const rearrangeClientImages = async (
  clientId: number,
  documents: Array<IUpdateDocumentArrangementsPayload>
) => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/documents-rearrange`,
    {
      body: {
        documents,
      },
    }
  );
  return data;
};

export interface ICategoryInput extends IFetchFavoritesInput {}

/** Fetch favorites */
export interface IFetchFavoritesInput {
  sortField?: string;
  page?: number;
  pageSize?: number;
  order?: 'asc' | 'desc';
}

export interface IFavoritesData {
  items: IProductListOutput['items'];
  paging: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const fetchFavorites = async (
  payload: IFetchFavoritesInput
): Promise<Types.ResponseOutputV2<IFavoritesData>> => {
  const query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/favourite-products${query}`
  );
  return data;
};

/** Saved Searches */
export interface IFetchSavedSearchesInput {
  sortField?: string;
  page?: number;
  pageSize?: number;
  order?: 'asc' | 'desc';
}

export interface IFetchClientRequestInput extends IFetchSavedSearchesInput {}
export interface IClientRequestData extends ISavedSearchesData {}
export interface IFetchTaskInput extends IFetchSavedSearchesInput {
  customer_id: string | number;
}

export interface ISavedSearchesData {
  items: Array<
    Partial<{
      id: number;
      guid: string;
      items: number;
      name: string;
      permissions: any[];
      params: any;
      status: number;
      type: number;
      channels: any[];
      updatedonutc: string;
      createdonutc: string;
      createdby: number;
      shareurl: string;
      sharedusers: any[];
      storeid: number;
      entitytype: string;
      creator: string;
      statusname: string;
      typename: string;
      customerid: number;
    }>
  >;
  paging: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export interface ITaskData {
  items: Array<
    Partial<{
      id: number;
      guid: string;
      items: number;
      name: string;
      permissions: any[];
      params: any;
      status: number;
      type: number;
      channels: any[];
      updatedonutc: string;
      createdonutc: string;
      createdby: number;
      shareurl: string;
      sharedusers: any[];
      storeid: number;
      entitytype: string;
      creator: string;
      statusname: string;
      typename: string;
      customerid: number;
    }>
  >;
  paging: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const fetchSavedSearches = async (
  payload: IFetchSavedSearchesInput
): Promise<Types.ResponseOutputV2<ISavedSearchesData>> => {
  const query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/saved-searches${query}`
  );
  return data;
};

export const fetchTask = async (
  payload: IFetchTaskInput
): Promise<Types.ResponseOutputV2<ISavedSearchesData>> => {
  const query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'customer_id', value: payload?.customer_id },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/list${query}`
  );
  return data;
};

export const fetchClientRequest = async (
  payload: IFetchClientRequestInput
): Promise<Types.ResponseOutputV2<IClientRequestData>> => {
  const query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/client-request${query}`
  );
  return data;
};

/** Client Collections */
export interface IClientCollectionsInput {
  type?: 'personal' | 'agent';
}

export interface IDeleteClientCollectionsInput {
  customer_id: string | number;
  sharing_type?: SharingType;
}

export type IClientCollectionsData = Array<
  Partial<{
    id: number;
    guid: string;
    items: number;
    name: string;
    permissions: any[];
    params: any;
    status: number;
    type: number;
    channels: any[];
    updatedonutc: string;
    createdonutc: string;
    createdby: number;
    shareurl: string;
    sharedusers: any[];
    storeid: number;
    entitytype: string;
    creator: string;
    statusname: string;
    typename: string;
    customerid: number;
    picture: string;
  }>
>;

export const fetchClientCollections = async (
  payload: IClientCollectionsInput
): Promise<Types.ResponseOutputV2<IClientCollectionsData>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/collections/${
      payload.type ? payload.type : ''
    }`
  );
  return data;
};
export const fetchSingleClientCollections = async (
  clientId: number | string,
  payload: IClientCollectionsInput
): Promise<Types.ResponseOutputV2<IClientCollectionsData>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/collections/${
      payload.type ? payload.type : ''
    }`
  );
  return data;
};

export const deleteSingleClientCollections = async (
  collectionId: number | string,
  payload: IDeleteClientCollectionsInput
): Promise<Types.ResponseOutputV2<IClientCollectionsData>> => {
  const query = varToStringParams({
    variablesArray: [
      { key: 'customer_id', value: payload?.customer_id },
      {
        key: 'sharing_type',
        value: payload?.sharing_type ?? SharingType.Collection,
      },
    ],
  });
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/customer/${collectionId}/collection${query}`
  );
  return data;
};

export const deleteSingleClientSavedSearch = async (
  collectionId: number | string,
  payload: IDeleteClientCollectionsInput
): Promise<Types.ResponseOutputV2<IClientCollectionsData>> => {
  const query = varToStringParams({
    variablesArray: [{ key: 'customer_id', value: payload?.customer_id }],
  });
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/customer/${collectionId}/saved-search${query}`
  );
  return data;
};

export const deleteSingleClientRequest = async (
  guid: number | string
): Promise<Types.ResponseOutputV2<IClientCollectionsData>> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/sharing/${guid}/client-request`
  );
  return data;
};

export interface IUpdateCustomerProfile {
  email?: string;
  firstname?: string;
  lastname?: string;
  gender?: number;
  dateofbirth?: Date;
  picture?: {
    mimetype: string;
    seofilename?: string;
    altattribute?: string;
    titleattribute?: string;
    virtualpath: string;
    originalfilename: string;
    filesize: number;
    visible: boolean;
    filetype: number;
    filecontenttype: number;
  };
}

export const updateCustomerProfile = async (
  clientId: string | number,
  payload: IUpdateCustomerProfile
): Promise<Types.ResponseOutput> => {
  const { data } = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/customer/profile`,
    {
      body: payload,
      showToastMessage: true,
    }
  );
  return data;
};

export const fetchCustomerByIdFavouriteProducts = async (
  clientId: number | string
): Promise<Types.ResponseOutputV2<IFavoritesData>> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/favourite-products`
  );
  return data;
};

export const fetchInterest = async (): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/interest`
  );
  return data;
};

export interface ICustomerTagListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  name: string;
}

export interface IDataItemsTags {
  id: number;
  name: string;
}

export interface ICustomerTagListOutput {
  items: IDataItemsTags[];
  paging: IPaging;
}

export interface ICustomerTagListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  name: string;
}

export const fetchCustomerTagList = async (
  payload: ICustomerTagListInput
): Promise<Types.ResponseOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'name', value: payload?.name },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customertag/list${query}`
  );

  let _data: ICustomerTagListOutput = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface ICustomerTagEditOutput extends Types.ResponseOutput {
  data: {};
}

export const deleteCustomerTag = async ({
  id,
}: {
  id: number;
}): Promise<ICustomerTagEditOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/customertag/${id}`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export interface ICustomerTagEditInput {
  name: string | null;
}

export const editCustomerTag = async (payload: {
  id: number;
  name: string;
}): Promise<ICustomerTagEditOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/customertag/${payload.id}`,
    { body: { name: payload.name } }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const fetchClientAttribute = async (payload: {
  customerAttribute: CustomerAttribute | string;
}): Promise<any> => {
  let query = varToStringParams({
    variablesArray: [
      {
        key: 'customerAttribute',
        value: payload.customerAttribute?.toString(),
      },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/attribute${query}`
  );

  return data;
};

export const fetchClientLocation = async (clientId: number) => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/location`
  );
  let _data: Array<INote> = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface IClientLocationInput {
  countryid: number;
  cityid: number;
  municipalityid: number;
  address1: string | null;
  address2: string | null;
  zippostalcode: string | null;
}

export const updateCustomerLocation = async (
  clientId: number,
  payload: IClientLocationInput
): Promise<Types.ResponseOutput> => {
  const { data } = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/customer/${clientId}/location`,
    {
      body: payload,
    }
  );
  return data;
};
